/** The POI-1 panels: what the engine is doing today, and what it decided.
 *
 * Self-gates on strategy type. Ordered as the day runs (docs/poi1.md §1): the
 * phase, the opening candle's side and level with the stop and target, then
 * the option held with its loss cap.
 *
 * ⚠️ Level, stop and target are INDEX prices; the loss cap is an OPTION price.
 */

import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trail } from "./DecisionTrail";
import { StrategySizeCard } from "./StrategySizeCard";
import { poi1Api, type Poi1Levels, type Poi1Position, type Poi1Status } from "@/lib/poi1Api";
import type { Strategy } from "@/types/strategy";

const STATUS_POLL_MS = 5_000;
const TRAIL_POLL_MS = 15_000;

const CHANNEL: Record<string, string> = {
  day: "Day",
  levels: "Levels",
  arming: "Arming",
  position: "Position",
  exit: "Exit",
  feed: "Feed",
};

const PHASE: Record<string, string> = {
  waiting: "Waiting for the 09:15–09:20 candle.",
  watching: "Watching for a 5-min close on the wrong side of the level.",
  armed: "Armed — buys when price comes back to the level.",
  in_trade:
    "In the trade — exits on the target, a 5-min close beyond the SL, the loss cap or 15:24.",
  done: "Done for the day.",
};

const SHAPE: Record<string, string> = {
  lower_wick: "lower wick",
  upper_wick: "upper wick",
  green_body: "green body",
  red_body: "red body",
};

export function Poi1Panels({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement | null {
  if (strategy.strategy_type !== "poi1") return null;
  return (
    <>
      <Poi1StatusCard strategyId={strategy.id} />
      <StrategySizeCard strategy={strategy} />
      <Poi1DecisionTrailCard strategyId={strategy.id} />
    </>
  );
}

function Poi1StatusCard({ strategyId }: Readonly<{ strategyId: string }>): ReactElement {
  const q = useQuery({
    queryKey: ["poi1-status", strategyId],
    queryFn: () => poi1Api.status(strategyId),
    refetchInterval: STATUS_POLL_MS,
  });
  const underlying = q.data?.underlying ?? null;
  return (
    <section className="card mb-3">
      <div className="mb-2 flex items-center gap-2">
        <i className="ti ti-target text-accent" />
        <span className="text-sm font-medium">POI-1 engine</span>
        {underlying !== null && <Chip text={underlying} />}
      </div>
      <Poi1Body data={q.data} loading={q.isPending} failed={q.isError} />
    </section>
  );
}

export function Poi1Body({
  data,
  loading,
  failed,
}: Readonly<{ data: Poi1Status | undefined; loading: boolean; failed: boolean }>): ReactElement {
  if (loading) return <p className="text-xs text-ink-muted">Reading the engine…</p>;
  if (failed || data === undefined) {
    return <p className="text-xs text-ink-muted">Could not read the engine&apos;s status.</p>;
  }
  if (!data.tracking) {
    return (
      <p className="text-xs text-ink-muted">
        Not tracking — the engine has not ticked for this strategy since the last restart. This is
        not the same as flat: check the trade log for an open trade.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-ink-secondary">
        {data.phase === null ? "Before the session." : PHASE[data.phase]}
        {data.over !== null && ` ${data.over}.`}
      </p>
      {data.levels !== null && <LevelsRow levels={data.levels} armedFrom={data.armed_from} />}
      {data.position !== null && <PositionRow position={data.position} entryAt={data.entry_at} />}
      <p className="text-[11px] text-ink-muted">
        <Figure label="spot" value={data.last_spot} /> · {tickText(data.last_tick_at)}
      </p>
    </div>
  );
}

function LevelsRow({
  levels,
  armedFrom,
}: Readonly<{ levels: Poi1Levels; armedFrom: string | null }>): ReactElement {
  return (
    <div className="rounded-ctl bg-surface-2 px-2 py-1.5 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-accent">{levels.side}</span>
        <Chip text={SHAPE[levels.shape] ?? levels.shape} />
        <Figure label="level" value={levels.level} />
        <Figure label="SL" value={levels.stop} />
        <Figure label="target" value={levels.target} />
        <Chip text={levels.widened ? "15-min edges" : "5-min edges"} accent={levels.widened} />
      </div>
      {armedFrom !== null && (
        <p className="mt-1 text-[11px] text-ink-muted">
          Armed — may fill from {armedFrom.slice(0, 5)}.
        </p>
      )}
    </div>
  );
}

function PositionRow({
  position,
  entryAt,
}: Readonly<{ position: Poi1Position; entryAt: string | null }>): ReactElement {
  const capBasis = position.cap_from_fill ? "from the fill" : "from the entry quote";
  return (
    <div className="rounded-ctl bg-surface-2 px-2 py-1.5 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium">{position.contract_symbol}</span>
        <span className="text-ink-secondary">
          {position.lots} lots × {position.lot_size}
        </span>
        <Figure label="loss cap at premium" value={position.cap_price} />
      </div>
      <p className="mt-1 text-[11px] text-ink-muted">
        {entryAt !== null && `Entered ${entryAt.slice(0, 5)}. `}
        Loss cap Rs {position.cap_rupees_per_lot.toLocaleString("en-IN")} a lot, {capBasis}.
      </p>
    </div>
  );
}

function Chip({
  text,
  accent = false,
}: Readonly<{ text: string; accent?: boolean }>): ReactElement {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] ${
        accent ? "bg-accent/10 text-accent" : "bg-surface-2 text-ink-muted"
      }`}
    >
      {text}
    </span>
  );
}

function Figure({ label, value }: Readonly<{ label: string; value: number | null }>): ReactElement {
  return (
    <span className="text-ink-secondary">
      <span className="text-ink-muted">{label} </span>
      <span className="font-mono tabular-nums">{value === null ? "—" : value.toFixed(2)}</span>
    </span>
  );
}

function tickText(at: string | null): string {
  if (at === null) return "no tick recorded yet";
  const time = new Date(at).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
  return `last tick ${time} IST`;
}

function labelOf(channel: string): string {
  return CHANNEL[channel] ?? channel;
}

function Poi1DecisionTrailCard({ strategyId }: Readonly<{ strategyId: string }>): ReactElement {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["poi1-decisions", strategyId],
    queryFn: () => poi1Api.decisions(strategyId),
    refetchInterval: TRAIL_POLL_MS,
    enabled: open,
  });
  return (
    <section className="card">
      <details
        open={open}
        onToggle={(e) => {
          setOpen(e.currentTarget.open);
        }}
      >
        <summary className="flex cursor-pointer select-none items-center gap-2">
          <i className="ti ti-list-search text-lg text-accent" />
          <h3 className="inline">Today&apos;s decisions</h3>
        </summary>
        <div className="mt-3">
          <Trail rows={q.data ?? []} loading={q.isPending} labelOf={labelOf} />
        </div>
      </details>
    </section>
  );
}
